/**
 * AppHeader — Top header with breadcrumbs (from route metadata registry) and user controls.
 *
 * Features:
 * - WAI-compliant breadcrumbs (nav landmark, aria-label, aria-current)
 * - Localized breadcrumb labels at render time (TRP-414)
 * - Localized document.title (TRP-414) — updates on locale switch
 * - "Admin Console" prefix for admin shell
 * - Mobile sidebar trigger
 * - Active muster session indicator (localized — TRP-414)
 *
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/breadcrumb/
 */

import React, { useMemo, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Separator } from '@/components/ui/separator';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { useUiStore } from '@/store/uiStore';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { MessageIcon } from '@/components/messaging/MessageIcon';
import { LanguageSelector } from '@/components/common/LanguageSelector';
import { buildBreadcrumbs, getRouteMetadata } from '@/config/routeMetadata';
import { useTranslationWithCMS } from '@/i18n/useTranslationWithCMS';
import { safeShellT } from '@/i18n/safeShellT';
import type { NavShell } from '@/config/nav';

interface BreadcrumbData {
  label: string;
  path: string;
}

/**
 * TRP-414 — render-time breadcrumb resolution.
 * Re-runs `buildBreadcrumbs` whenever pathname OR active locale changes,
 * so language switches reactively update the breadcrumb labels without a
 * route reload. The dependency on `i18n.language` is the locale-change
 * signal; `t` itself is stable across renders (memoized by i18next).
 */
function useBreadcrumbs(): BreadcrumbData[] {
  const location = useLocation();
  const { t, i18n } = useTranslationWithCMS();

  return useMemo(
    () => buildBreadcrumbs(location.pathname, undefined, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname, i18n.language, t],
  );
}

/**
 * TRP-414 — render-time document.title.
 * Depends on `i18n.language` so locale switches update the title without a
 * route reload. The suffix "AMuster" is preserved verbatim per operator
 * brand policy (translated to itself in ar.json `shell.docTitle.suffix`).
 */
function usePageTitle() {
  const location = useLocation();
  const { t, i18n } = useTranslationWithCMS();

  useEffect(() => {
    const meta = getRouteMetadata(location.pathname);
    const suffix = safeShellT(t, 'shell.docTitle.suffix', { fallback: 'AMuster' });
    if (meta) {
      const fallbackTitle = meta.pageTitle;
      const pageTitle = meta.pageTitleKey
        ? safeShellT(t, meta.pageTitleKey, { fallback: fallbackTitle })
        : fallbackTitle;
      document.title = `${pageTitle} - ${suffix}`;
    } else {
      document.title = suffix;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, i18n.language, t]);
}

export function AppHeader({ shell: _shell }: { shell?: NavShell }) {
  const { activeMusterSessionId } = useUiStore();
  const breadcrumbs = useBreadcrumbs();
  const { t } = useTranslationWithCMS();

  // Sync document.title from route metadata (TRP-414: render-time + locale-aware)
  usePageTitle();

  // TRP-414 — breadcrumb primitive accessibility text
  const breadcrumbAriaLabel = safeShellT(t, 'shell.breadcrumb.ariaLabel', {
    fallback: 'breadcrumb',
  });
  const activeMusterAlertText = safeShellT(t, 'shell.alert.activeMuster', {
    fallback: 'Active Muster Session in Progress',
  });

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center border-b bg-background">
      {/* Active muster alert */}
      {activeMusterSessionId && (
        <div className="absolute inset-x-0 top-0 flex items-center justify-center gap-2 bg-destructive px-4 py-1.5 text-sm font-medium text-destructive-foreground">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
          </span>
          {activeMusterAlertText}
        </div>
      )}

      <div className="flex w-full items-center justify-between px-4">
        <div className="flex items-center gap-2">
          {/* Mobile/collapsed sidebar trigger */}
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />

          {/* WAI-compliant Breadcrumbs (TRP-414: localized aria-label) */}
          <Breadcrumb ariaLabel={breadcrumbAriaLabel}>
            <BreadcrumbList>
              {breadcrumbs.map((crumb, idx) => {
                const isLast = idx === breadcrumbs.length - 1;

                return (
                  <React.Fragment key={crumb.path}>
                    {idx > 0 && <BreadcrumbSeparator />}
                    <BreadcrumbItem>
                      {isLast ? (
                        <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                      ) : (
                        <BreadcrumbLink asChild>
                          <Link to={crumb.path}>{crumb.label}</Link>
                        </BreadcrumbLink>
                      )}
                    </BreadcrumbItem>
                  </React.Fragment>
                );
              })}
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        {/* Right-side controls */}
        <div className="flex items-center gap-1">
          <LanguageSelector />
          <MessageIcon />
          <NotificationBell />
        </div>
      </div>
    </header>
  );
}
